<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';
import BarChart from 'shared/components/charts/BarChart.vue';
import { bucketLabel } from '../../helper/crmBuckets';
import ReportMetricCard from '../ReportMetricCard.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import ConversionFunnelAPI from 'dashboard/api/reports/conversionFunnel';
import CrmContactsReportsAPI from 'dashboard/api/reports/crmContacts';

const { t } = useI18n();

const DATE_FILTERS = {
  TODAY: 'today',
  LAST_WEEK: 'last_week',
  LAST_MONTH: 'last_month',
  CUSTOM: 'custom'
};

const selectedDateFilter = ref(null);
const customDateRange = ref([new Date(), new Date()]);
const isLoading = ref(false);
// Resumen del embudo de conversión: misma fuente que la pantalla "Embudo".
// El conteo de leads sale de SQL sobre toda la tabla contacts, no del listado paginado.
const funnelSummary = ref(null);
// Agregados de contactos (crecimiento, nuevos vs recurrentes, con/sin negocio).
// También resueltos en SQL: antes se calculaban sobre la primera página del listado.
const contactsSummary = ref(null);

const dateFilterOptions = computed(() => [
  { id: DATE_FILTERS.TODAY, name: t('CRM_REPORTS.LEADS.FILTERS.TODAY') },
  { id: DATE_FILTERS.LAST_WEEK, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_WEEK') },
  { id: DATE_FILTERS.LAST_MONTH, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_MONTH') },
  { id: DATE_FILTERS.CUSTOM, name: t('CRM_REPORTS.LEADS.FILTERS.CUSTOM') }
]);

onMounted(() => {
  selectedDateFilter.value = dateFilterOptions.value[0];
});

const dateRange = computed(() => {
  if (!selectedDateFilter.value) return { start: new Date(), end: new Date() };
  
  const today = new Date();
  
  switch (selectedDateFilter.value.id) {
    case DATE_FILTERS.TODAY:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
    case DATE_FILTERS.LAST_WEEK:
      return {
        start: startOfDay(subDays(today, 7)),
        end: endOfDay(today)
      };
    case DATE_FILTERS.LAST_MONTH:
      return {
        start: startOfDay(subDays(today, 30)),
        end: endOfDay(today)
      };
    case DATE_FILTERS.CUSTOM:
      return {
        start: startOfDay(customDateRange.value[0]),
        end: endOfDay(customDateRange.value[1])
      };
    default:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
  }
});

const unixRange = () => {
  const { start, end } = dateRange.value;
  return {
    since: Math.floor(start.getTime() / 1000),
    until: Math.floor(end.getTime() / 1000)
  };
};

// Mismo endpoint agregado que usa la pantalla de Embudo de conversión
// (V2::Reports::Crm::ConversionFunnelBuilder). Devuelve leads_created ya contados
// en la base y la serie diaria lista para el gráfico.
const fetchFunnelSummary = async () => {
  try {
    const { data } = await ConversionFunnelAPI.getSummary({
      ...unixRange(),
      granularity: 'day'
    });
    funnelSummary.value = data;
  } catch (error) {
    funnelSummary.value = null;
  }
};

// Agregados de contactos, acotados al mismo rango que el resto de la pantalla.
const fetchContactsSummary = async () => {
  try {
    const { data } = await CrmContactsReportsAPI.getSummary(unixRange());
    contactsSummary.value = data;
  } catch (error) {
    contactsSummary.value = null;
  }
};

// Un solo disparador para las dos fuentes: todo lo que muestra la pantalla depende
// del filtro de fechas.
const fetchAll = async () => {
  isLoading.value = true;
  try {
    await Promise.all([fetchFunnelSummary(), fetchContactsSummary()]);
  } finally {
    isLoading.value = false;
  }
};

watch(dateRange, fetchAll);

const leadsCount = computed(
  () => funnelSummary.value?.leads_created?.count ?? 0
);

const leadsWithOpenDealsCount = computed(
  () => funnelSummary.value?.leads_with_open_deals?.count ?? 0
);

// Las series del embudo se piden siempre con granularidad diaria.
const timeseriesLabel = timestamp => bucketLabel(timestamp, 'day');

const leadsCreatedChartData = computed(() => {
  const points = funnelSummary.value?.leads_timeseries || [];

  return {
    labels: points.map(p => timeseriesLabel(p.timestamp)),
    datasets: [
      {
        label: t('CRM_REPORTS.LEADS.CHART.LABEL'),
        data: points.map(p => p.value),
        backgroundColor: 'rgb(59, 130, 246)',
        borderColor: 'rgb(37, 99, 235)',
        borderWidth: 1
      }
    ]
  };
});

const leadsWithOpenDealsChartData = computed(() => {
  const points = funnelSummary.value?.leads_with_open_deals_timeseries || [];

  return {
    labels: points.map(p => timeseriesLabel(p.timestamp)),
    datasets: [
      {
        label: t('CRM_REPORTS.LEADS.METRICS.WITH_OPEN_DEALS.LABEL'),
        data: points.map(p => p.value),
        backgroundColor: 'rgb(59, 130, 246)',
        borderColor: 'rgb(37, 99, 235)',
        borderWidth: 1
      }
    ]
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      enabled: true
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1
      }
    }
  }
};

const onDateRangeChange = (value) => {
  customDateRange.value = value;
};

watch(selectedDateFilter, (newValue) => {
  if (newValue && newValue.id !== DATE_FILTERS.CUSTOM) {
    customDateRange.value = [new Date(), new Date()];
  }
}, { deep: true });

// ===== CONTACT GROWTH SECTION =====

// El backend arma tres ventanas comparables del mismo largo, todas derivadas del filtro
// de fechas: la seleccionada (current), la inmediatamente anterior (previous) y la misma
// corrida un año atrás (last_year). Cada una trae sus buckets ya alineados por índice.
const growth = computed(() => contactsSummary.value?.growth || {});
const rangeGranularity = computed(
  () => contactsSummary.value?.range?.granularity || 'day'
);

// Etiqueta de una ventana: "01/08/26 – 26/08/26". Reemplaza al nombre del mes, que ya no
// aplica cuando el rango puede ser un día, una semana o dos años.
const windowLabel = key => {
  const window = growth.value[key];
  if (!window) return '—';
  const start = new Date(window.start * 1000);
  const end = new Date(window.end * 1000);
  return `${format(start, 'dd/MM/yy')} – ${format(end, 'dd/MM/yy')}`;
};

// Computed - Labels
const currentMonthLabel = computed(() => windowLabel('current'));
const previousMonthLabel = computed(() => windowLabel('previous'));
const lastYearMonthLabel = computed(() => windowLabel('last_year'));

// Computed - Counts
const currentMonthCount = computed(() => growth.value.current?.count ?? 0);
const previousMonthCount = computed(() => growth.value.previous?.count ?? 0);
const lastYearMonthCount = computed(() => growth.value.last_year?.count ?? 0);

const percentageChange = (current, reference) => {
  if (!reference) return null;
  return Math.round(((current - reference) / reference) * 100);
};

// Computed - Changes (percentage)
const previousMonthChange = computed(() =>
  percentageChange(currentMonthCount.value, previousMonthCount.value)
);

const lastYearChange = computed(() =>
  percentageChange(currentMonthCount.value, lastYearMonthCount.value)
);

const labelFor = timestamp => bucketLabel(timestamp, rangeGranularity.value);

// Computed - Detalle por bucket
// Las tres ventanas tienen la misma granularidad, así que el bucket N de `previous` es el
// equivalente al bucket N de `current`. Si una ventana quedó más corta (meses de distinto
// largo), el bucket faltante se muestra en 0.
const weeklyData = computed(() => {
  const current = growth.value.current?.points || [];
  const previous = growth.value.previous?.points || [];
  const lastYear = growth.value.last_year?.points || [];

  return current.map((point, index) => ({
    label: labelFor(point.timestamp),
    current: point.value,
    previous: previous[index]?.value ?? 0,
    lastYear: lastYear[index]?.value ?? 0
  }));
});

// Computed - Chart Data
const contactGrowthChartData = computed(() => {
  return {
    labels: weeklyData.value.map(w => w.label),
    datasets: [
      {
        label: currentMonthLabel.value,
        data: weeklyData.value.map(w => w.current),
        backgroundColor: 'rgba(59, 130, 246, 0.7)', // blue-500
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1
      },
      {
        label: previousMonthLabel.value,
        data: weeklyData.value.map(w => w.previous),
        backgroundColor: 'rgba(168, 85, 247, 0.7)', // purple-500
        borderColor: 'rgba(168, 85, 247, 1)',
        borderWidth: 1
      },
      {
        label: lastYearMonthLabel.value,
        data: weeklyData.value.map(w => w.lastYear),
        backgroundColor: 'rgba(245, 158, 11, 0.7)', // amber-500
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 1
      }
    ]
  };
});

// ===== NEW VS RECURRING CONTACTS SECTION =====

// Recurrente = el contacto es primary_contact de al menos un negocio cerrado.
// La clasificación la resuelve el backend; acá solo se leen los totales.
const contactTotals = computed(() => contactsSummary.value?.totals || {});

// Computed - Counts y Percentages
const newContactsCount = computed(() => contactTotals.value.new ?? 0);
const recurringContactsCount = computed(() => contactTotals.value.recurring ?? 0);
const totalContacts = computed(() => contactTotals.value.contacts ?? 0);

const percentageOfTotal = (value, total) =>
  total === 0 ? 0 : Math.round((value / total) * 100);

const newContactsPercentage = computed(() =>
  percentageOfTotal(newContactsCount.value, totalContacts.value)
);

const recurringContactsPercentage = computed(() =>
  percentageOfTotal(recurringContactsCount.value, totalContacts.value)
);

// Computed - Detalle por bucket del rango seleccionado
const monthlyNewVsRecurringData = computed(() =>
  (contactsSummary.value?.new_vs_recurring || []).map(bucket => ({
    label: labelFor(bucket.timestamp),
    new: bucket.new,
    recurring: bucket.recurring,
    total: bucket.total
  }))
);

// Computed - Chart Data (Stacked Bar Chart)
const newVsRecurringChartData = computed(() => {
  return {
    labels: monthlyNewVsRecurringData.value.map(m => m.label),
    datasets: [
      {
        label: t('CRM_REPORTS.NEW_VS_RECURRING.NEW_CONTACTS'),
        data: monthlyNewVsRecurringData.value.map(m => m.new),
        backgroundColor: 'rgba(34, 197, 94, 0.7)', // green-500
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1
      },
      {
        label: t('CRM_REPORTS.NEW_VS_RECURRING.RECURRING_CONTACTS'),
        data: monthlyNewVsRecurringData.value.map(m => m.recurring),
        backgroundColor: 'rgba(59, 130, 246, 0.7)', // blue-500
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1
      }
    ]
  };
});

// Chart Options para gráfico stacked
const stackedChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false
    }
  },
  scales: {
    x: {
      stacked: true,
      grid: {
        display: false
      }
    },
    y: {
      stacked: true,
      beginAtZero: true,
      ticks: {
        precision: 0
      },
      grid: {
        color: 'rgba(0, 0, 0, 0.05)'
      }
    }
  }
};

// ===== CONTACTS WITH/WITHOUT DEALS SECTION =====

// Con negocio = el contacto es primary_contact de al menos un negocio, en cualquier estado.

// Computed - Counts
const contactsWithDealsCount = computed(() => contactTotals.value.with_deals ?? 0);
const contactsWithoutDealsCount = computed(
  () => contactTotals.value.without_deals ?? 0
);
const totalContactsForDeals = computed(() => totalContacts.value);

// Computed - Percentages
const contactsWithDealsPercentage = computed(() =>
  percentageOfTotal(contactsWithDealsCount.value, totalContactsForDeals.value)
);

const contactsWithoutDealsPercentage = computed(() =>
  percentageOfTotal(contactsWithoutDealsCount.value, totalContactsForDeals.value)
);

// Computed - Detalle por bucket del rango seleccionado
const monthlyDealsComparison = computed(() =>
  (contactsSummary.value?.deals_split || []).map(bucket => ({
    label: labelFor(bucket.timestamp),
    withDeals: bucket.with_deals,
    withoutDeals: bucket.without_deals,
    total: bucket.total
  }))
);

// Computed - Chart data for deals comparison
const dealsComparisonChartData = computed(() => {
  return {
    labels: monthlyDealsComparison.value.map(m => m.label),
    datasets: [
      {
        label: t('CRM_REPORTS.CONTACTS_DEALS.WITH_DEALS'),
        data: monthlyDealsComparison.value.map(m => m.withDeals),
        backgroundColor: 'rgba(34, 197, 94, 0.7)', // green-500
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1
      },
      {
        label: t('CRM_REPORTS.CONTACTS_DEALS.WITHOUT_DEALS'),
        data: monthlyDealsComparison.value.map(m => m.withoutDeals),
        backgroundColor: 'rgba(139, 92, 246, 0.7)', // violet-500
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 1
      }
    ]
  };
});

// Chart Options para gráfico de comparación
const dealsComparisonChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        footer: (tooltipItems) => {
          const total = tooltipItems.reduce((sum, item) => sum + item.parsed.y, 0);
          return `Total: ${total}`;
        }
      }
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      }
    },
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0
      },
      grid: {
        color: 'rgba(0, 0, 0, 0.05)'
      }
    }
  }
};
</script>

<template>
  <div class="space-y-6">
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('CRM_REPORTS.LEADS.TITLE') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {{ $t('CRM_REPORTS.LEADS.DESCRIPTION') }}
          </p>
        </div>
        
        <div class="flex items-center gap-4">
          <div class="multiselect-wrap--small w-48">
            <multiselect
              v-model="selectedDateFilter"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')"
              :options="dateFilterOptions"
              :searchable="false"
              :allow-empty="false"
              :show-labels="false"
            />
          </div>
          
          <WootDateRangePicker
            v-if="selectedDateFilter && selectedDateFilter.id === DATE_FILTERS.CUSTOM"
            :value="customDateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            @change="onDateRangeChange"
          />
        </div>
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <ReportMetricCard
          :label="$t('CRM_REPORTS.LEADS.METRICS.CREATED.LABEL')"
          :value="leadsCount.toString()"
          :info-text="$t('CRM_REPORTS.LEADS.METRICS.CREATED.INFO')"
        />
        <ReportMetricCard
          :label="$t('CRM_REPORTS.LEADS.METRICS.WITH_OPEN_DEALS.LABEL')"
          :value="leadsWithOpenDealsCount.toString()"
          :info-text="$t('CRM_REPORTS.LEADS.METRICS.WITH_OPEN_DEALS.INFO')"
        />
      </div>
      
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          {{ $t('CRM_REPORTS.LEADS.CHART.TITLE') }}
        </h4>
        <div class="h-80">
          <BarChart :collection="leadsCreatedChartData" :chart-options="chartOptions" />
        </div>
      </div>

      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          {{ $t('CRM_REPORTS.LEADS.METRICS.WITH_OPEN_DEALS.LABEL') }}
        </h4>
        <div class="h-80">
          <BarChart :collection="leadsWithOpenDealsChartData" :chart-options="chartOptions" />
        </div>
      </div>
    </div>

    <!-- Sección de Crecimiento de Contactos -->
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="mb-6">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ $t('CRM_REPORTS.CONTACT_GROWTH.TITLE') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
          {{ $t('CRM_REPORTS.CONTACT_GROWTH.DESCRIPTION') }}
        </p>
      </div>

      <!-- Métricas Comparativas -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <!-- Mes Actual -->
        <div class="metric-card bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{ currentMonthLabel }}
              </p>
              <p class="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
                {{ currentMonthCount }}
              </p>
            </div>
            <div class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="calendar-ltr" size="24" class="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <!-- Mes Anterior -->
        <div class="metric-card bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{ previousMonthLabel }}
              </p>
              <p class="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
                {{ previousMonthCount }}
              </p>
              <div v-if="previousMonthChange !== null" class="flex items-center mt-1">
                <fluent-icon 
                  :icon="previousMonthChange >= 0 ? 'arrow-up' : 'arrow-down'" 
                  size="16"
                  :class="previousMonthChange >= 0 ? 'text-green-600' : 'text-red-600'"
                />
                <span 
                  class="text-sm font-medium ml-1"
                  :class="previousMonthChange >= 0 ? 'text-green-600' : 'text-red-600'"
                >
                  {{ Math.abs(previousMonthChange) }}%
                </span>
              </div>
            </div>
            <div class="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="calendar-month" size="24" class="text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <!-- Mismo Mes Año Pasado -->
        <div class="metric-card bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{ lastYearMonthLabel }}
              </p>
              <p class="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
                {{ lastYearMonthCount }}
              </p>
              <div v-if="lastYearChange !== null" class="flex items-center mt-1">
                <fluent-icon 
                  :icon="lastYearChange >= 0 ? 'arrow-up' : 'arrow-down'" 
                  size="16"
                  :class="lastYearChange >= 0 ? 'text-green-600' : 'text-red-600'"
                />
                <span 
                  class="text-sm font-medium ml-1"
                  :class="lastYearChange >= 0 ? 'text-green-600' : 'text-red-600'"
                >
                  {{ Math.abs(lastYearChange) }}%
                </span>
              </div>
            </div>
            <div class="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="calendar-year" size="24" class="text-amber-600 dark:text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      <!-- Gráfico de Barras por Semana -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          {{ $t('CRM_REPORTS.CONTACT_GROWTH.WEEKLY_CHART_TITLE') }}
        </h4>
        <div class="h-80">
          <BarChart v-if="contactGrowthChartData.datasets.length > 0" :collection="contactGrowthChartData" :chart-options="chartOptions" />
          <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
            {{ $t('CRM_REPORTS.NO_DATA') }}
          </div>
        </div>
      </div>

      <!-- Tabla de Detalle Semanal -->
      <div class="mt-6 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <table class="w-full">
          <thead class="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ $t('CRM_REPORTS.CONTACT_GROWTH.WEEK') }}
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ currentMonthLabel }}
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ previousMonthLabel }}
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ lastYearMonthLabel }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900">
            <tr v-for="(week, index) in weeklyData" :key="index" class="hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td class="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">
                {{ week.label }}
              </td>
              <td class="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right font-medium">
                {{ week.current }}
              </td>
              <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 text-right">
                {{ week.previous }}
              </td>
              <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 text-right">
                {{ week.lastYear }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Sección de Contactos Nuevos vs Recurrentes -->
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="mb-6">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ $t('CRM_REPORTS.NEW_VS_RECURRING.TITLE') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
          {{ $t('CRM_REPORTS.NEW_VS_RECURRING.DESCRIPTION') }}
        </p>
      </div>

      <!-- Métricas Resumen -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <!-- Contactos Nuevos -->
        <div class="metric-card bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{ $t('CRM_REPORTS.NEW_VS_RECURRING.NEW_CONTACTS') }}
              </p>
              <p class="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
                {{ newContactsCount }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ newContactsPercentage }}% del total
              </p>
            </div>
            <div class="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="person-add" size="24" class="text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <!-- Contactos Recurrentes -->
        <div class="metric-card bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{ $t('CRM_REPORTS.NEW_VS_RECURRING.RECURRING_CONTACTS') }}
              </p>
              <p class="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
                {{ recurringContactsCount }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ recurringContactsPercentage }}% del total
              </p>
            </div>
            <div class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="people" size="24" class="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
      </div>

      <!-- Gráfico Stacked por Mes -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          {{ $t('CRM_REPORTS.NEW_VS_RECURRING.MONTHLY_CHART_TITLE') }}
        </h4>
        <div class="h-80">
          <BarChart v-if="newVsRecurringChartData.datasets.length > 0" :collection="newVsRecurringChartData" :chart-options="stackedChartOptions" />
          <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
            {{ $t('CRM_REPORTS.NO_DATA') }}
          </div>
        </div>
      </div>

      <!-- Tabla de Detalle Mensual -->
      <div class="mt-6 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <table class="w-full">
          <thead class="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ $t('CRM_REPORTS.NEW_VS_RECURRING.MONTH') }}
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ $t('CRM_REPORTS.NEW_VS_RECURRING.NEW_CONTACTS') }}
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ $t('CRM_REPORTS.NEW_VS_RECURRING.RECURRING_CONTACTS') }}
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Total
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900">
            <tr v-for="(month, index) in monthlyNewVsRecurringData" :key="index" class="hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td class="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">
                {{ month.label }}
              </td>
              <td class="px-6 py-4 text-sm text-green-600 dark:text-green-400 text-right font-medium">
                {{ month.new }}
              </td>
              <td class="px-6 py-4 text-sm text-blue-600 dark:text-blue-400 text-right font-medium">
                {{ month.recurring }}
              </td>
              <td class="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right font-semibold">
                {{ month.total }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Contactos con Negocio vs Sin Negocio -->
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="mb-6">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          {{ $t('CRM_REPORTS.CONTACTS_DEALS.TITLE') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
          {{ $t('CRM_REPORTS.CONTACTS_DEALS.DESCRIPTION') }}
        </p>
      </div>

      <!-- Métricas Resumen -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <!-- Contactos con Negocios -->
        <div class="metric-card bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{ $t('CRM_REPORTS.CONTACTS_DEALS.WITH_DEALS') }}
              </p>
              <p class="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
                {{ contactsWithDealsCount }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ contactsWithDealsPercentage }}% del total
              </p>
            </div>
            <div class="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="briefcase" size="24" class="text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <!-- Contactos sin Negocios -->
        <div class="metric-card bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{ $t('CRM_REPORTS.CONTACTS_DEALS.WITHOUT_DEALS') }}
              </p>
              <p class="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
                {{ contactsWithoutDealsCount }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ contactsWithoutDealsPercentage }}% del total
              </p>
            </div>
            <div class="w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="person" size="24" class="text-violet-600 dark:text-violet-400" />
            </div>
          </div>
        </div>
      </div>

      <!-- Gráfico de Comparación Mensual -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          {{ $t('CRM_REPORTS.CONTACTS_DEALS.MONTHLY_CHART_TITLE') }}
        </h4>
        <div class="h-80">
          <BarChart v-if="dealsComparisonChartData.datasets.length > 0" :collection="dealsComparisonChartData" :chart-options="dealsComparisonChartOptions" />
          <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
            {{ $t('CRM_REPORTS.NO_DATA') }}
          </div>
        </div>
      </div>

      <!-- Tabla de Detalle Mensual -->
      <div class="mt-6 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <table class="w-full">
          <thead class="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ $t('CRM_REPORTS.CONTACTS_DEALS.MONTH') }}
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ $t('CRM_REPORTS.CONTACTS_DEALS.WITH_DEALS') }}
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ $t('CRM_REPORTS.CONTACTS_DEALS.WITHOUT_DEALS') }}
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Total
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900">
            <tr v-for="(month, index) in monthlyDealsComparison" :key="index" class="hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td class="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">
                {{ month.label }}
              </td>
              <td class="px-6 py-4 text-sm text-green-600 dark:text-green-400 text-right font-medium">
                {{ month.withDeals }}
              </td>
              <td class="px-6 py-4 text-sm text-violet-600 dark:text-violet-400 text-right font-medium">
                {{ month.withoutDeals }}
              </td>
              <td class="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right font-semibold">
                {{ month.total }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
